import { inject, injectable } from 'inversify';
import { RpcSuccess } from '@dealroadshow/json-rpc-dispatcher';
import Request from '@framework/api/Rpc/Request';
import { type IDispatcher } from '@dm-portal/json-rpc-dispatcher';
import DITypes from '@dm-portal/json-rpc-dispatcher/DITypes';
import { IRoadshowSubmitData } from '@drs/domain/IRoadshowSubmitData';
import { IRoadshowData, IRoadshowSettings, IRoadshowActivityItem } from '@drs/domain/IRoadshowData';
import { IFetchCollectionPayload } from '@framework/state/useFetchCollection';

@injectable()
class RoadshowRepository {
  constructor(
    @inject(DITypes.JsonRpcDispatcher) private readonly rpc: IDispatcher,
  ) {}

  async getList(filters: IFetchCollectionPayload): Promise<{
    data: IRoadshowActivityItem[],
    totalCount: number,
  }> {
    const defaultFilters = {
      sortBy: 'name',
      sortOrder: 'asc',
    };

    if (filters.perPage === 'all') {
      const { perPage, page, ...otherFilters } = filters;
      filters = otherFilters;
    }

    filters = { ...defaultFilters, ...filters };
    let req = new Request('roadshow.list.manager', filters);
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  /**
   * Get retail roadshows list
   *
   * @param {Object} payload
   */
  async getRetailList(payload): Promise<void> {
    let req = new Request('roadshow.list.retail', payload);
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  async getRoadshowById(roadshowId: string): Promise<IRoadshowData> {
    let req = new Request('roadshow.get_by_id', { roadshowId });
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  async getRoadshowSettingsById(roadshowId: string): Promise<IRoadshowSettings> {
    let req = new Request('roadshow.get_settings_by_id', { roadshowId });
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  /**
   * Get presentable roadshow by id
   *
   */
  async getPresentableRoadshowById(roadshowId: string): Promise<any> {
    let req = new Request('roadshow.get_presentable_by_id', { roadshowId });
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  /**
   * Get presentable roadshow by id
   *
   */
  async getPreviewPresentableRoadshowById(roadshowId: string): Promise<any> {
    let req = new Request('roadshow.get_preview_presentable_by_id', { roadshowId });
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  /**
   * Get presentable retail roadshow by id
   *
   */
  async getPresentableRetailRoadshowById(roadshowId: string): Promise<any> {
    let req = new Request('roadshow.get_presentable_retail_by_id', { roadshowId });
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  /**
   * Get presentable roadshow by entry code
   *
   */
  async getPresentableRoadshowByEntryCode(entryCode: string): Promise<any> {
    let req = new Request('roadshow.get_presentable_by_entry_code', { entryCode });
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  async getAdminRoadshowMapping(roadshowId: string): Promise<any> {
    let req = new Request('admin.roadshow.get_roadshow_mapping', { roadshowId });
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  /**
   * @param {Object} roadshowMapping
   */
  async updateRoadshowMapping(roadshowMapping): Promise<any> {
    let req = new Request('admin.roadshow.set_roadshow_mapping', roadshowMapping);
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  async getAdminDealSlides(roadshowId: string): Promise<any> {
    let req = new Request('admin.roadshow.get_deal_slides', { roadshowId });
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  /**
   * @param {Object} dealSlides
   */
  async updateAdminDealSlides(dealSlides): Promise<any> {
    let req = new Request('admin.roadshow.set_deal_slides', dealSlides);
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  /**
   * @param {Object} dealSlides
   */
  async moveAdminDealSlides(dealSlides): Promise<any> {
    let req = new Request('admin.roadshow.move_deal_slides', dealSlides);
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  /**
   * Get public access url
   *
   * @param {String} url
   * @param {?String} cdnType
   */
  async getTemporaryAssetUrl(url: string, cdnType = null): Promise<any> {
    if (!url) {
      return null;
    }
    let req = new Request('roadshow.get_temporary_url', { url, cdnType });
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  /**
   * Toggle roadshow published state
   *
   */
  async togglePublished(roadshowId: string, published: boolean): Promise<void> {
    let req = new Request('roadshow.toggle_published', { roadshowId, published });
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  async create(data: IRoadshowSubmitData): Promise<any> {
    let req = new Request('roadshow.create', data);
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  async update(data: IRoadshowSubmitData): Promise<any> {
    let req = new Request('roadshow.update', data);
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  async getDialInCode(payload: { count: number }): Promise<any> {
    const req = new Request('roadshow.get_generated_dial_in_codes', payload);
    const response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  /**
   * Update the dashboard link id
   *
   */
  async updateDashboardLink(payload: { roadshowId: string, evercallChatId: string }): Promise<any> {
    let req = new Request('roadshow.update_dashboard_link', payload);
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  /**
   * Save company name or underwriter
   *
   * @param {Object} payload
   */
  async saveAccount(payload): Promise<any> {
    let req = new Request('dm_portal.request_to_create_account', payload);
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }

  async getViewerCustomSettings(payload): Promise<any> {
    let req = new Request('roadshow.get_viewer_custom_settings', payload);
    let response = await this.rpc.call<RpcSuccess>(req);

    return response.getResult().payload;
  }
}

export default RoadshowRepository;
